import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProduct, getProductFiles, deleteProduct } from '../lib/api';
import { useAuth } from '../lib/auth';
import { 
    formatCurrency, 
    formatDate, 
    getStatusColor, 
    getStatusLabel,
} from '../lib/utils';
import {
    ArrowLeft,
    Pencil,
    Trash,
    Package,
    CurrencyDollar,
    BoxArrowDown,
    Wrench,
    Tag,
    Image as ImageIcon
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { FileSection } from '../components/FileSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DataRow({ label, value, mono = false }) {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="grid grid-cols-3 gap-4 py-2.5 border-b border-border/50 last:border-0" data-testid={`data-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <dt className="text-sm text-muted-foreground">{label}</dt>
            <dd className={`col-span-2 text-sm ${mono ? 'font-mono' : ''}`}>{value}</dd>
        </div>
    );
}

function SectionCard({ title, icon: Icon, children }) {
    return (
        <div className="bg-card border border-border rounded-lg">
            <div className="flex items-center gap-2 px-5 py-3.5 border-b border-border">
                <Icon size={18} className="text-muted-foreground" />
                <h3 className="font-semibold text-sm">{title}</h3>
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function ProductImage({ image, productName }) {
    const [imgError, setImgError] = useState(false);
    const src = image?.url || image?.file_url || (image?.storage_path && image?.id
        ? `${API_URL}/api/files/${image.id}/download?auth=${localStorage.getItem('token')}`
        : null);
    
    if (!src || imgError) {
        return (
            <div className="w-full aspect-square rounded-lg bg-muted/50 border border-border flex flex-col items-center justify-center gap-2" data-testid="product-image-placeholder">
                <ImageIcon size={48} className="text-muted-foreground/40" />
                <span className="text-xs text-muted-foreground">No image available</span>
            </div>
        );
    }
    return (
        <img src={src} alt={productName} className="w-full aspect-square rounded-lg object-cover border border-border" onError={() => setImgError(true)} data-testid="product-image" />
    );
}

function StatBlock({ label, value, sub, mono = false }) {
    return (
        <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
            <p className={`text-lg font-semibold ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</p>
            {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
    );
}

export default function ProductDetail() {
    const { productKey } = useParams();
    const navigate = useNavigate();
    const { isAdmin, canViewSection } = useAuth();
    
    const [product, setProduct] = useState(null);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [productRes, filesRes] = await Promise.all([
                    getProduct(productKey),
                    getProductFiles(productKey)
                ]);
                setProduct(productRes.data);
                setFiles(filesRes.data);
            } catch (error) {
                console.error('Failed to fetch product:', error);
                if (error.response?.status === 404) {
                    toast.error('Product not found');
                    navigate('/products');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [productKey, navigate]);

    const handleDelete = async () => {
        try {
            await deleteProduct(productKey);
            toast.success('Product deleted');
            navigate('/products');
        } catch (error) {
            toast.error('Failed to delete product');
        }
    };

    const refreshFiles = async () => {
        const res = await getProductFiles(productKey);
        setFiles(res.data);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-64" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-64 lg:col-span-2" />
                    <Skeleton className="h-64" />
                </div>
            </div>
        );
    }

    if (!product) return null;

    const specs = product.specifications || {};
    const packaging = product.packaging || {};
    const pricing = product.pricing || [];
    
    const exportPrices = pricing.filter(p => p.price_basis === 'export' || p.price_type === 'export');
    const eurPrice = exportPrices.find(p => p.currency === 'EUR') || pricing.find(p => p.currency === 'EUR');
    const usdPrice = exportPrices.find(p => p.currency === 'USD') || pricing.find(p => p.currency === 'USD');
    const primaryPrice = eurPrice || usdPrice || pricing[0];

    const rawDims = specs.size_mm || (specs.length_mm && specs.width_mm ? `${specs.length_mm} x ${specs.width_mm}` : null);
    const dimensions = rawDims ? (rawDims.toString().toLowerCase().includes('mm') ? rawDims : `${rawDims} mm`) : null;
    const thickness = specs.thickness_text || (specs.thickness_mm ? `${specs.thickness_mm} mm` : null);

    return (
        <div className="space-y-6" data-testid="product-detail-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
                        <Link to="/products">
                            <ArrowLeft size={16} className="mr-1" /> Back
                        </Link>
                    </Button>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-bold tracking-tight" data-testid="product-title">{product.product_name}</h1>
                        <Badge className={getStatusColor(product.status)} data-testid="product-status">
                            {getStatusLabel(product.status)}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-sm text-muted-foreground flex-wrap">
                        <span className="font-mono font-semibold text-foreground" data-testid="product-sku">{product.display_sku}</span>
                        {product.brand && <><span className="text-border">|</span><span>{product.brand}</span></>}
                        {product.category && <><span className="text-border">|</span><span>{product.category}</span></>}
                        {product.decor_no && <><span className="text-border">|</span><span className="font-mono">Decor {product.decor_no}</span></>}
                    </div>
                    <p className="text-xs text-muted-foreground/60 mt-1 font-mono">{product.product_key}</p>
                </div>

                {isAdmin() && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Button variant="outline" asChild data-testid="edit-product-btn">
                            <Link to={`/products/${productKey}/edit`}>
                                <Pencil size={16} className="mr-2" /> Edit
                            </Link>
                        </Button>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="destructive" size="icon" data-testid="delete-product-btn">
                                    <Trash size={16} />
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Product</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Delete {product.display_sku}? This removes all related data and cannot be undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-muted">
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    {canViewSection('pricing') && <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>}
                    {canViewSection('packaging') && <TabsTrigger value="packaging" data-testid="tab-packaging">Packaging</TabsTrigger>}
                    {canViewSection('specifications') && <TabsTrigger value="specifications" data-testid="tab-specifications">Specifications</TabsTrigger>}
                    <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                </TabsList>

                {/* ===== OVERVIEW TAB ===== */}
                <TabsContent value="overview" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Left Column */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Commercial Summary Card */}
                            <div className="bg-card border border-border rounded-lg p-5">
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                                    <StatBlock label="EUR Export Price" value={product.eur_price ? formatCurrency(product.eur_price, 'EUR') : null} mono />
                                    <StatBlock label="MOQ (EUR 5K)" value={product.computed_moq?.toLocaleString()} sub={packaging.carton_qty ? `${packaging.carton_qty}/carton` : null} mono />
                                    <StatBlock label="Dimensions" value={dimensions} mono />
                                    <StatBlock label="Thickness" value={thickness} mono />
                                </div>
                            </div>

                            {/* Product Information */}
                            <SectionCard title="Product Information" icon={Package}>
                                <dl>
                                    <DataRow label="Display SKU" value={product.display_sku} mono />
                                    <DataRow label="Product Name" value={product.product_name} />
                                    <DataRow label="Brand" value={product.brand} />
                                    <DataRow label="Category" value={product.category} />
                                    <DataRow label="Sub-category" value={product.subcategory} />
                                    <DataRow label="Decor No" value={product.decor_no} mono />
                                    <DataRow label="Decor Name" value={product.decor_name} />
                                    <DataRow label="Finish" value={product.finish || specs.finish} />
                                    <DataRow label="Shape" value={product.shape} />
                                    <DataRow label="Description" value={product.description} />
                                    <DataRow label="Variant Group" value={product.variant_group} mono />
                                    <DataRow label="Source Sheet" value={product.source_sheet} />
                                    <DataRow label="Internal Remarks" value={product.internal_remarks} />
                                </dl>
                            </SectionCard>

                            {/* Key Specs */}
                            <SectionCard title="Key Specifications" icon={Wrench}>
                                <dl>
                                    <DataRow label="Size" value={specs.size_mm} />
                                    <DataRow label="Length" value={specs.length_mm ? `${specs.length_mm} mm` : null} mono />
                                    <DataRow label="Width" value={specs.width_mm ? `${specs.width_mm} mm` : null} mono />
                                    <DataRow label="Thickness" value={thickness} mono />
                                    <DataRow label="Finish" value={specs.finish} />
                                    <DataRow label="Material" value={specs.material_type} />
                                    <DataRow label="Type" value={specs.type} />
                                    <DataRow label="Weight" value={
                                        packaging.unit_weight_kg ? `${packaging.unit_weight_kg} kg` 
                                        : packaging.unit_weight_g ? `${packaging.unit_weight_g} g` 
                                        : null
                                    } mono />
                                </dl>
                            </SectionCard>
                        </div>

                        {/* Right Column / Sidebar */}
                        <div className="space-y-6">
                            {/* Product Image */}
                            <div className="bg-card border border-border rounded-lg p-4">
                                <ProductImage image={product.primary_image} productName={product.product_name} />
                            </div>

                            {/* Pricing Summary */}
                            <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="pricing-sidebar">
                                <h3 className="font-semibold text-sm flex items-center gap-2">
                                    <CurrencyDollar size={16} className="text-muted-foreground" /> Pricing
                                </h3>
                                {pricing.length > 0 ? (
                                    <div className="space-y-3">
                                        {eurPrice && (
                                            <div>
                                                <p className="text-xs text-muted-foreground">EUR Export</p>
                                                <p className="text-2xl font-bold font-mono" data-testid="eur-price">{formatCurrency(eurPrice.price, 'EUR')}</p>
                                            </div>
                                        )}
                                        {usdPrice && (
                                            <div>
                                                <p className="text-xs text-muted-foreground">USD Export</p>
                                                <p className="text-xl font-semibold font-mono" data-testid="usd-price">{formatCurrency(usdPrice.price, 'USD')}</p>
                                            </div>
                                        )}
                                        {product.computed_moq && (
                                            <div className="pt-2 border-t border-border/50">
                                                <p className="text-xs text-muted-foreground">MOQ (min EUR 5,000)</p>
                                                <p className="text-xl font-bold font-mono" data-testid="moq-value">{product.computed_moq.toLocaleString()} pcs</p>
                                            </div>
                                        )}
                                        <div>
                                            <p className="text-xs text-muted-foreground">Unit of Sale</p>
                                            <p className="text-sm font-medium capitalize">{primaryPrice?.unit_of_sale || '—'}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground">No pricing data</p>
                                )}
                            </div>

                            {/* Decor & Categories */}
                            {(product.decor_no || product.category) && (
                                <div className="bg-card border border-border rounded-lg p-5 space-y-3" data-testid="decor-sidebar">
                                    <h3 className="font-semibold text-sm flex items-center gap-2">
                                        <Tag size={16} className="text-muted-foreground" /> Classification
                                    </h3>
                                    {product.decor_no && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Decor No</p>
                                            <p className="font-mono font-semibold" data-testid="decor-no">{product.decor_no}</p>
                                        </div>
                                    )}
                                    {product.decor_name && (
                                        <div>
                                            <p className="text-xs text-muted-foreground">Decor Name</p>
                                            <p className="font-medium">{product.decor_name}</p>
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                        {product.category && <Badge variant="outline">{product.category}</Badge>}
                                        {product.subcategory && <Badge variant="secondary">{product.subcategory}</Badge>}
                                    </div>
                                </div>
                            )}
                            
                            {/* Metadata */}
                            <div className="bg-card border border-border rounded-lg p-5 space-y-2 text-xs text-muted-foreground">
                                {product.created_at && <div>Created: {formatDate(product.created_at)}</div>}
                                {product.updated_at && <div>Updated: {formatDate(product.updated_at)}</div>}
                            </div>
                        </div>
                    </div>
                </TabsContent>

                {/* ===== PRICING TAB ===== */}
                {canViewSection('pricing') && (
                    <TabsContent value="pricing" className="mt-6">
                        <SectionCard title="Pricing Records" icon={CurrencyDollar}>
                            {pricing.length === 0 ? (
                                <p className="text-muted-foreground text-sm">No pricing records</p>
                            ) : (
                                <div className="space-y-4">
                                    {pricing.map((price, idx) => (
                                        <div key={idx} className="p-4 bg-muted/30 rounded-lg">
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="capitalize">{price.price_type}</Badge>
                                                    {price.price_basis && <Badge variant="secondary" className="capitalize">{price.price_basis}</Badge>}
                                                </div>
                                                <span className="text-2xl font-semibold font-mono">{formatCurrency(price.price, price.currency)}</span>
                                            </div>
                                            <dl className="grid grid-cols-2 gap-2 text-sm">
                                                <div><span className="text-muted-foreground">Currency:</span> {price.currency}</div>
                                                <div><span className="text-muted-foreground">Unit:</span> {price.unit_of_sale || '—'}</div>
                                                {price.base_price != null && (
                                                    <div><span className="text-muted-foreground">Base Price{price.base_price_label ? ` (${price.base_price_label})` : ''}:</span> <span className="font-mono">{price.base_price}</span></div>
                                                )}
                                                {price.addon_price != null && (
                                                    <div><span className="text-muted-foreground">Add-on{price.addon_price_label ? ` (${price.addon_price_label})` : ''}:</span> <span className="font-mono">{price.addon_price}</span></div>
                                                )}
                                                {price.yield_qty_per_board != null && (
                                                    <div><span className="text-muted-foreground">Yield/Board:</span> <span className="font-mono">{price.yield_qty_per_board}</span></div>
                                                )}
                                                {price.thickness_mm != null && (
                                                    <div><span className="text-muted-foreground">Thickness:</span> <span className="font-mono">{price.thickness_mm} mm</span></div>
                                                )}
                                                {price.price_notes && (
                                                    <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {price.price_notes}</div>
                                                )}
                                            </dl>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </SectionCard>
                    </TabsContent>
                )}

                {/* ===== PACKAGING TAB ===== */}
                {canViewSection('packaging') && (
                    <TabsContent value="packaging" className="mt-6">
                        <SectionCard title="Packaging Information" icon={BoxArrowDown}>
                            {!packaging || Object.keys(packaging).length === 0 ? (
                                <p className="text-muted-foreground text-sm">No packaging data</p>
                            ) : (
                                <dl>
                                    <DataRow label="Unit Weight" value={
                                        packaging.unit_weight_kg ? `${packaging.unit_weight_kg} kg` 
                                        : packaging.unit_weight_g ? `${packaging.unit_weight_g} g` 
                                        : null
                                    } mono />
                                    <DataRow label="Packing Type" value={packaging.packing_type || packaging.unit_packing_type} />
                                    <DataRow label="Packaging Status" value={packaging.packaging_status} />
                                    <DataRow label="Inner Qty" value={packaging.inner_qty || packaging.inner_pack_qty} mono />
                                    <DataRow label="Carton Qty" value={packaging.carton_qty || packaging.master_carton_qty} mono />
                                    <DataRow label="Carton Dimensions" value={
                                        packaging.carton_length_cm && packaging.carton_width_cm && packaging.carton_height_cm
                                            ? `${packaging.carton_length_cm} x ${packaging.carton_width_cm} x ${packaging.carton_height_cm} cm`
                                            : null
                                    } mono />
                                    <DataRow label="Net Weight" value={packaging.net_weight_kg ? `${packaging.net_weight_kg} kg` : null} mono />
                                    <DataRow label="Gross Weight" value={packaging.gross_weight_kg ? `${packaging.gross_weight_kg} kg` : null} mono />
                                    <DataRow label="CBM" value={packaging.cbm} mono />
                                    <DataRow label="Notes" value={packaging.notes || packaging.packaging_notes} />
                                    <DataRow label="Fragile/Handling" value={packaging.fragile_notes} />
                                    <DataRow label="Source" value={packaging.source_sheet} />
                                </dl>
                            )}
                        </SectionCard>
                    </TabsContent>
                )}

                {/* ===== SPECIFICATIONS TAB ===== */}
                {canViewSection('specifications') && (
                    <TabsContent value="specifications" className="mt-6">
                        <SectionCard title="Full Specifications" icon={Wrench}>
                            {!specs || Object.keys(specs).length === 0 ? (
                                <p className="text-muted-foreground text-sm">No specification data</p>
                            ) : (
                                <dl>
                                    <DataRow label="Size" value={specs.size_mm || specs.size_label} />
                                    <DataRow label="Length" value={specs.length_mm ? `${specs.length_mm} mm` : null} mono />
                                    <DataRow label="Width" value={specs.width_mm ? `${specs.width_mm} mm` : null} mono />
                                    <DataRow label="Thickness" value={specs.thickness_text || (specs.thickness_mm ? `${specs.thickness_mm} mm` : null)} mono />
                                    <DataRow label="Finish" value={specs.finish} />
                                    <DataRow label="Type" value={specs.type} />
                                    <DataRow label="Material Type" value={specs.material_type} />
                                    <DataRow label="Groove Count" value={specs.groove_count} mono />
                                    <DataRow label="Bowl Count" value={specs.bowl_count} mono />
                                    <DataRow label="Bowl Type" value={specs.bowl_type} />
                                    <DataRow label="Top Sheet" value={specs.top_sheet} />
                                    <DataRow label="Handle Type" value={specs.handle_type} />
                                    <DataRow label="Stand Material" value={specs.stand_material} />
                                    <DataRow label="Stand Size" value={specs.stand_size_mm} />
                                    <DataRow label="Board Size" value={specs.board_size_mm} />
                                    <DataRow label="Working Size" value={specs.working_size_mm} />
                                    <DataRow label="Plate 1 Size" value={specs.plate1_size_mm} />
                                    <DataRow label="Plate 2 Size" value={specs.plate2_size_mm} />
                                    <DataRow label="Plate 3 Size" value={specs.plate3_size_mm} />
                                    <DataRow label="No. of Boards" value={specs.no_of_boards} mono />
                                    <DataRow label="Special Features" value={specs.special_features} />
                                    <DataRow label="Care Instructions" value={specs.care_instructions} />
                                    <DataRow label="Usage Notes" value={specs.usage_notes} />
                                    <DataRow label="Source Sheet" value={specs.source_sheet} />
                                </dl>
                            )}
                        </SectionCard>
                    </TabsContent>
                )}

                {/* ===== DOCUMENTS TAB ===== */}
                <TabsContent value="documents" className="mt-6">
                    <FileSection 
                        productKey={productKey}
                        files={files}
                        onRefresh={refreshFiles}
                    />
                </TabsContent>
            </Tabs>
        </div>
    );
}
